import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getEvent, getScanStats, getRecentArrivals } from '../services/api';
import { Users, Megaphone, CheckCircle, XCircle, Clock, ArrowLeft, Scan, MapPin, Calendar, Copy, KeyRound } from 'lucide-react';
import toast from 'react-hot-toast';
import API from '../services/api';

function StatCard({ val, lbl, color, icon: Icon }) {
  return (
    <div className="stat-card" style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
      <div style={{ width: 44, height: 44, borderRadius: 12, background: `${color}18`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
        <Icon size={20} color={color} />
      </div>
      <div>
        <div className="val" style={{ color, fontSize: 26 }}>{val}</div>
        <div className="lbl">{lbl}</div>
      </div>
    </div>
  );
}

export default function EventDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [event, setEvent] = useState(null);
  const [scanStats, setScanStats] = useState(null);
  const [arrivals, setArrivals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pin, setPin] = useState('');
  const [savingPin, setSavingPin] = useState(false);

  const scannerUrl = `${window.location.origin}/scanner/?event=${id}`;

  const load = async () => {
    try {
      const [evRes, scanRes, arrRes] = await Promise.all([
        getEvent(id),
        getScanStats(id),
        getRecentArrivals(id),
      ]);
      setEvent(evRes.data);
      setScanStats(scanRes.data);
      setArrivals(arrRes.data);
      setPin(evRes.data.scanner_pin || '');
    } catch { toast.error('Erreur chargement'); }
    finally { setLoading(false); }
  };

  useEffect(() => {
    load();
    const interval = setInterval(load, 15000);
    return () => clearInterval(interval);
  }, [id]);

  const handleSavePin = async () => {
    if (pin && (pin.length !== 4 || !/^\d+$/.test(pin))) {
      return toast.error('Le PIN doit être 4 chiffres');
    }
    setSavingPin(true);
    try {
      await API.patch(`/scan/pin/${id}`, { pin: pin || null });
      toast.success(pin ? 'PIN enregistré' : 'PIN supprimé');
    } catch { toast.error('Erreur'); }
    finally { setSavingPin(false); }
  };

  const copyLink = () => {
    navigator.clipboard.writeText(scannerUrl);
    toast.success('Lien copié !');
  };

  if (loading) return <div style={{ display: 'flex', justifyContent: 'center', padding: 80 }}><div className="spinner" /></div>;
  if (!event) return null;

  const rsvpDone = (+event.confirmed || 0) + (+event.declined || 0);
  const rsvpPct  = event.total_invited > 0 ? Math.round((rsvpDone / event.total_invited) * 100) : 0;
  const checkinPct = event.confirmed > 0 ? Math.round(((+scanStats?.checked_in || 0) / +event.confirmed) * 100) : 0;

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 24 }}>
        <button className="btn btn-ghost btn-sm" onClick={() => navigate('/events')}>
          <ArrowLeft size={14} /> Retour
        </button>
      </div>

      <div className="page-header">
        <div>
          <h1>{event.name}</h1>
          <div style={{ display: 'flex', gap: 16, marginTop: 6, flexWrap: 'wrap' }}>
            {event.event_date && (
              <span className="text-muted text-sm" style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                <Calendar size={13} />
                {new Date(event.event_date).toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
              </span>
            )}
            {event.location && (
              <span className="text-muted text-sm" style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                <MapPin size={13} /> {event.location}
              </span>
            )}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          <button className="btn btn-ghost" onClick={() => navigate(`/events/${id}/guests`)}>
            <Users size={15} /> Invités
          </button>
          <button className="btn btn-primary" onClick={() => navigate(`/events/${id}/campaigns`)}>
            <Megaphone size={15} /> Campagnes
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="stats-grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))' }}>
        <StatCard val={event.total_invited || 0} lbl="Total invités"      color="var(--accent)"  icon={Users} />
        <StatCard val={event.confirmed || 0}     lbl="Confirmés"          color="var(--green)"   icon={CheckCircle} />
        <StatCard val={event.declined || 0}      lbl="Déclinés"           color="var(--red)"     icon={XCircle} />
        <StatCard val={event.pending || 0}       lbl="En attente"         color="var(--amber)"   icon={Clock} />
        <StatCard val={scanStats?.checked_in || 0} lbl="Arrivés (jour J)" color="var(--accent2)" icon={Scan} />
      </div>

      {/* Progression */}
      <div className="card" style={{ marginBottom: 20 }}>
        <h2 style={{ marginBottom: 20 }}>Progression</h2>
        <div style={{ marginBottom: 20 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
            <span className="text-sm">Taux de réponse RSVP</span>
            <span className="text-sm" style={{ color: 'var(--accent)', fontWeight: 600 }}>{rsvpPct}%</span>
          </div>
          <div className="progress-bar"><div className="progress-fill" style={{ width: `${rsvpPct}%` }} /></div>
        </div>
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
            <span className="text-sm">Check-in jour J</span>
            <span className="text-sm" style={{ color: 'var(--accent2)', fontWeight: 600 }}>{checkinPct}%</span>
          </div>
          <div className="progress-bar">
            <div className="progress-fill" style={{ width: `${checkinPct}%`, background: 'linear-gradient(90deg, var(--accent2), #a78bfa)' }} />
          </div>
        </div>
      </div>

      {/* Scanner jour J */}
      <div className="card" style={{ marginBottom: 20 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
          <div style={{ width: 40, height: 40, borderRadius: 10, background: 'rgba(34,197,94,0.12)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Scan size={20} color="var(--green)" />
          </div>
          <div>
            <h2 style={{ fontSize: 17 }}>Scanner QR — Jour J</h2>
            <p className="text-sm text-muted">Lien à partager avec les agents à l'entrée</p>
          </div>
        </div>

        {/* Lien scanner */}
        <div style={{ display: 'flex', gap: 8, marginBottom: 16, alignItems: 'center' }}>
          <div style={{ flex: 1, background: 'var(--bg3)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: '9px 13px', fontSize: 13, color: 'var(--text2)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {scannerUrl}
          </div>
          <button className="btn btn-ghost btn-sm" onClick={copyLink} title="Copier le lien">
            <Copy size={14} />
          </button>
          <a href={scannerUrl} target="_blank" rel="noreferrer" className="btn btn-success btn-sm">
            <Scan size={14} /> Ouvrir
          </a>
        </div>

        {/* PIN */}
        <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end' }}>
          <div style={{ flex: 1 }}>
            <label style={{ display: 'block', fontSize: 12.5, fontWeight: 500, color: 'var(--text2)', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              <KeyRound size={11} style={{ marginRight: 4 }} />
              Code PIN (4 chiffres, optionnel)
            </label>
            <input
              type="number"
              maxLength={4}
              placeholder="Laisser vide = accès libre"
              value={pin}
              onChange={e => setPin(e.target.value.slice(0, 4))}
              style={{ letterSpacing: 6, fontSize: 18, textAlign: 'center' }}
            />
          </div>
          <button className="btn btn-primary" onClick={handleSavePin} disabled={savingPin} style={{ flexShrink: 0 }}>
            {savingPin ? <span className="spinner" /> : 'Enregistrer'}
          </button>
        </div>
        <p className="text-sm text-muted" style={{ marginTop: 8 }}>
          Le PIN est demandé aux agents avant d'accéder au scanner. Laissez vide pour un accès sans code.
        </p>
      </div>

      {/* Dernières arrivées */}
      {arrivals.length > 0 && (
        <div className="card">
          <h2 style={{ marginBottom: 16 }}>Dernières arrivées</h2>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {arrivals.map((g, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 12px', background: 'var(--bg3)', borderRadius: 'var(--radius)' }}>
                <div style={{ width: 32, height: 32, borderRadius: '50%', background: 'rgba(34,197,94,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <CheckCircle size={15} color="var(--green)" />
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 500 }}>{g.first_name} {g.last_name}</div>
                  {g.extra_data?.table && <div className="text-sm text-muted">{g.extra_data.table}</div>}
                </div>
                <div className="text-sm text-muted">
                  {g.checked_in_at ? new Date(g.checked_in_at).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' }) : ''}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
